<script setup lang="ts">
import {computed} from "vue"
import {Icon} from "@hantera/design-system"
import type {AppInfo} from "@/backend/apps.types"

const props = defineProps<{
  apps: AppInfo[]
  selectedAppId: string | null
  loading: boolean
}>()

const emit = defineEmits<{
  (e: "select", appId: string | null): void
}>()

const isHomeSelected = computed(() => props.selectedAppId === null)

function selectHome() {
  emit("select", null)
}

function selectApp(appId: string) {
  emit("select", appId)
}

function diagnosticCount(app: AppInfo): number {
  if (app.runtimeState !== "broken") return 0
  return app.diagnostics.length
}

function diagnosticTitle(count: number): string {
  return count === 1 ? "1 diagnostic" : `${count} diagnostics`
}
</script>

<template>
  <div class="app-sidebar">
    <div class="sidebar-section">
      <div
        class="sidebar-item home-item"
        :class="{ 'sidebar-item--selected': isHomeSelected }"
        @click="selectHome"
      >
        <Icon name="home" class="sidebar-item-icon" />
        <span class="sidebar-item-label">Home</span>
      </div>
    </div>

    <div class="sidebar-divider" />

    <div class="sidebar-section">
      <div class="sidebar-header">Installed Apps</div>

      <div v-if="loading" class="sidebar-loading">
        Loading...
      </div>

      <template v-else>
        <div
          v-for="app in apps.toSorted((a, b) => a.name.localeCompare(b.name))"
          :key="app.id"
          class="sidebar-item"
          :class="{
            'sidebar-item--selected': selectedAppId === app.id,
            'sidebar-item--broken': diagnosticCount(app) > 0,
          }"
          @click="selectApp(app.id)"
        >
          <Icon name="blocks" class="sidebar-item-icon" />
          <div class="sidebar-item-content">
            <span class="sidebar-item-label">{{ app.name }}</span>
            <span v-if="app.currentVersion" class="sidebar-item-sub-label">
              {{ app.currentVersion }}
            </span>
          </div>
          <Icon
            v-if="diagnosticCount(app) > 0"
            name="warning"
            class="sidebar-item-diagnostic"
            :title="diagnosticTitle(diagnosticCount(app))"
          />
        </div>

        <div v-if="apps.length === 0" class="sidebar-empty">
          No apps installed
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.app-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0.5rem 0;
}

.sidebar-item--broken .sidebar-item-label {
  color: var(--color-error);
}

.sidebar-item-diagnostic {
  margin-left: auto;
  flex-shrink: 0;
  width: 18px;
  color: var(--color-error);
}
</style>
